import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatDateTime,
  formatNumber,
  formatShortDate,
  messages,
  type CompanySummary,
  type ListSummary,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useDebounced, useSelection } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ResponsiveList, RowChevron, type Column } from '@/components/common/responsive-list';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { BulkBar, PageHeader } from '@/components/common/page';
import { ContactIcons } from '@/features/companies/contact-icons';
import { ListFormDialog } from '@/features/lists/list-form-dialog';
import { ListTypeBadge } from '@/features/lists/list-type-badge';

/** 条件リストの絞り込みキーの表示名（企業一覧の絞り込みと同じ語） */
const FILTER_LABELS: Record<string, string> = {
  q: '検索',
  status: '状況',
  assigneeId: '担当',
  tagIds: 'タグ',
  prefecture: '地域',
  city: '市区町村',
  industry: '業種',
  hasEmail: 'メール',
  hasForm: 'フォーム',
  hasPhone: '電話',
  hasAnyContact: '連絡手段',
  securityRisk: 'セキュリティ',
  securityChecked: 'セキュリティチェック',
  excluded: '対象外',
  listId: 'リスト',
  lowConfidenceOnly: '確度が低い項目',
  lastContactedBefore: '最終接触',
  createdAfter: '追加日',
  source: '収集元',
};

/** S33 リスト詳細。所属企業の確認と、静的リストの出し入れ */
export const ListDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole, user } = useAuth();
  const selection = useSelection();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const { data: listData, isLoading } = useQuery({
    queryKey: qk.list(id),
    queryFn: () => api.get<SingleResponse<ListSummary>>(`/lists/${id}`),
    enabled: !!id,
  });
  const list = listData?.data;

  const membersQuery = { listId: id, q: debouncedSearch, limit: 50, sort: '-updatedAt' };
  // ページを react-query に持たせる。企業詳細から戻ったときに、読み込み済みのページごと即座に復元される
  const {
    data: members,
    isLoading: membersLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: qk.companies(membersQuery),
    queryFn: ({ pageParam }) =>
      api.get<ListResponse<CompanySummary>>('/companies', { ...membersQuery, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.nextCursor : null),
    enabled: !!id,
  });
  const rows = useMemo(() => members?.pages.flatMap((page) => page.data) ?? [], [members]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
    void queryClient.invalidateQueries({ queryKey: qk.list(id) });
    void queryClient.invalidateQueries({ queryKey: qk.lists });
  };

  const removeItems = useMutation({
    mutationFn: () =>
      api.del<SingleResponse<{ applied: number }>>(`/lists/${id}/items`, {
        companyIds: [...selection.selected],
      }),
    onSuccess: (result) => {
      toast.success(messages.list.removedFromList(result.data.applied));
      selection.clear();
      setRemoveOpen(false);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const removeList = useMutation({
    mutationFn: () => api.del(`/lists/${id}`),
    onSuccess: () => {
      toast.success(`「${list?.name}」を削除しました`);
      void queryClient.invalidateQueries({ queryKey: qk.lists });
      navigate('/lists', { replace: true });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading || !list) return <SkeletonRows rows={6} />;

  const isStatic = list.type === 'static';
  // 編集・削除は作成者かマネージャー以上（API 側と同じ条件。押せない理由を表示するために手元でも判定する）
  const canEdit = hasRole('manager') || (!!list.createdBy && list.createdBy.id === user?.id);
  const editBlocked = canEdit ? null : 'リストの編集は作成者またはマネージャー以上が行えます';
  const deleteBlocked =
    list.usedByCampaignCount > 0 ? messages.list.inUse : editBlocked;

  const filterEntries = Object.entries(list.filterJson ?? {}).filter(([, v]) => v !== '' && v != null);
  const filterQuery = new URLSearchParams(filterEntries.map(([k, v]) => [k, String(v)])).toString();

  const columns: Column<CompanySummary>[] = [
    {
      key: 'name',
      header: '企業名',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground" title={row.name}>
            {row.name}
          </p>
          <p className="truncate text-note text-foreground-subtle">
            {[row.domain, [row.prefecture, row.city].filter(Boolean).join('')]
              .filter(Boolean)
              .join('・') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: '状況',
      width: '112px',
      render: (row) => <StatusBadge kind="company_status" value={row.status} />,
    },
    {
      key: 'industry',
      header: '業種',
      width: '176px',
      render: (row) => (
        <span className="block truncate" title={row.industry ?? undefined}>
          {row.industry ?? <span className="text-foreground-subtle">—</span>}
        </span>
      ),
    },
    {
      key: 'contacts',
      header: '連絡先',
      width: '92px',
      render: (row) => <ContactIcons company={row} />,
    },
    {
      key: 'lastContactedAt',
      header: '最終接触',
      width: '96px',
      render: (row) =>
        row.lastContactedAt ? (
          <span className="tabular">{formatShortDate(row.lastContactedAt)}</span>
        ) : (
          <span className="text-foreground-subtle">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        back="/lists"
        title={list.name}
        badge={<ListTypeBadge type={list.type} size="lg" />}
        description={
          <>
            <span className="tabular">{formatNumber(list.companyCount)} 社</span>
            {list.usedByCampaignCount > 0 &&
              ` ・ キャンペーン ${formatNumber(list.usedByCampaignCount)} 件で使用中`}
            {list.createdBy?.name && ` ・ 作成 ${list.createdBy.name}`}
            {' ・ 更新 '}
            <span className="tabular">{formatDateTime(list.updatedAt)}</span>
          </>
        }
        actions={
          <>
            {isStatic && (
              <Button variant="outline" onClick={() => navigate('/companies')}>
                <Plus aria-hidden />
                {messages.list.addFromCompanies}
              </Button>
            )}
            {hasRole('manager') && (
              <Button variant="outline" onClick={() => navigate(`/campaigns/new?listId=${list.id}`)}>
                <Send aria-hidden />
                {messages.campaign.create}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              disabled={!!editBlocked}
              disabledReason={editBlocked ?? undefined}
            >
              <Pencil aria-hidden />
              {messages.common.edit}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              disabled={!!deleteBlocked}
              disabledReason={deleteBlocked ?? undefined}
            >
              <Trash2 aria-hidden />
              {messages.common.delete}
            </Button>
          </>
        }
      />

      {list.description && (
        <p className="mb-3 whitespace-pre-wrap text-body text-foreground-muted">{list.description}</p>
      )}

      {!isStatic && (
        <Card className="mb-4">
          <CardContent className="space-y-2 pt-4">
            <p className="text-body text-foreground-muted">{messages.list.dynamicReadonly}</p>
            {filterEntries.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {filterEntries.map(([key, value]) => (
                  <li
                    key={key}
                    className="rounded-control border border-border-strong px-2 py-0.5 text-note text-foreground"
                  >
                    {FILTER_LABELS[key] ?? key}={String(value)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-note text-foreground-subtle">絞り込み条件はありません（全企業が対象）</p>
            )}
            <Button
              variant="link"
              size="sm"
              onClick={() => navigate(`/companies${filterQuery ? `?${filterQuery}` : ''}`)}
            >
              {messages.list.openInCompanies}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="mb-3">
        <Input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          placeholder="社名・ドメイン・電話で検索"
          aria-label="リスト内の企業を検索"
        />
      </div>

      <ResponsiveList
        columns={columns}
        rows={rows}
        layout="fixed"
        isLoading={membersLoading}
        selection={
          isStatic
            ? {
                selected: selection.selected,
                onToggle: selection.toggle,
                onToggleAll: (checked) => selection.toggleAll(rows.map((r) => r.id), checked),
              }
            : undefined
        }
        onRowClick={(row) => navigate(`/companies/${row.id}`)}
        hasMore={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        isLoadingMore={isFetchingNextPage}
        empty={
          debouncedSearch ? (
            <EmptyState
              title={messages.empty.companiesFiltered.title}
              description={messages.empty.companiesFiltered.description}
              action={{ label: messages.empty.companiesFiltered.action, onClick: () => setSearch('') }}
              icon={<Building2 className="size-8" />}
            />
          ) : isStatic ? (
            <EmptyState
              title="このリストには企業がありません"
              description={messages.list.emptyStatic}
              action={{ label: messages.list.addFromCompanies, onClick: () => navigate('/companies') }}
              icon={<Building2 className="size-8" />}
            />
          ) : (
            <EmptyState
              title={messages.empty.companiesFiltered.title}
              description="条件リストの絞り込みに一致する企業がありません"
              icon={<Building2 className="size-8" />}
            />
          )
        }
        card={(row) => (
          <button type="button" className="w-full text-left" onClick={() => navigate(`/companies/${row.id}`)}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate text-body font-semibold">{row.name}</span>
                  <StatusBadge kind="company_status" value={row.status} size="lg" />
                </div>
                <p className="truncate text-note text-foreground-muted">
                  {[row.industry, [row.prefecture, row.city].filter(Boolean).join('')]
                    .filter(Boolean)
                    .join('・') || '—'}
                </p>
                <ContactIcons company={row} showLabels />
              </div>
              <RowChevron />
            </div>
          </button>
        )}
      />

      {isStatic && (
        <BulkBar count={selection.count} onClear={selection.clear}>
          <Button variant="outline" size="sm" onClick={() => setRemoveOpen(true)}>
            <Trash2 aria-hidden className="size-4" />
            {messages.list.removeFromList}
          </Button>
        </BulkBar>
      )}

      <ListFormDialog open={editOpen} onOpenChange={setEditOpen} list={list} />

      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title={messages.list.removeFromList}
        summary={`${messages.list.removeSelected(selection.count)}。企業のデータは残ります。`}
        confirmText={messages.list.removeFromList}
        onConfirm={() => removeItems.mutate()}
        isPending={removeItems.isPending}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={messages.list.delete}
        summary={messages.list.confirmDelete(list.name)}
        confirmText={messages.list.delete}
        irreversible
        onConfirm={() => removeList.mutate()}
        isPending={removeList.isPending}
      />
    </>
  );
};
